import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IPersonCertificate, ICourse, IEducation, IPersonCompetence } from 'types/cvsInterfaces'
import EducationListItem from './EducationListItem'
import { useUser } from 'hooks'

export interface StyleProps {
  color: string
}

/** @notExported */
interface EducationItemCardProps {
  /** Function to open item. */
  openItem: (item: IEducation | ICourse | IPersonCertificate | IPersonCompetence) => void
  /** Item title. */
  title: string
  /** Item color. */
  editable: boolean
  /** Is item editable. */
  color: string
  /** Education items. */
  educations?: IEducation[]
  /** Course items. */
  courses?: ICourse[]
  /** Certificate items. */
  certificates?: IPersonCertificate[]
  /** Competence items. */
  competences?: IPersonCompetence[]
  /** Is item anonymous. */
  anonymous: boolean
}

/**
 * Education item card.
 *
 * @param openItem -
 * @param title -
 * @param color -
 * @param editable -
 * @param educations -
 * @param courses -
 * @param certificates -
 * @param anonymous -
 * @returns Education item card component.
 * @notExported
 */
const EducationItemCard: React.FC<EducationItemCardProps> = ({
  openItem,
  color,
  editable,
  title,
  educations,
  courses,
  certificates,
  competences,
  anonymous,
}) => {
  const { t, i18n } = useTranslation()
  const { features } = useUser()

  const educationsMap = useMemo(() => {
    if (title === 'degrees' && educations) {
      const length = educations.length
      return educations.map((education, i) => {
        return (
          <EducationListItem
            key={`${education.id}-${i18n.language}`}
            profileWarningId={`profile-item-education-${education.id}`}
            editable={editable}
            title={title}
            education={education}
            showDivider={i++ < length - 1}
            onOpen={() => openItem(education)}
            header="degree"
            anonymous={anonymous}
          />
        )
      })
    } else return []
  }, [anonymous, editable, educations, openItem, title, i18n.language])

  const coursesMap = useMemo(() => {
    if (title === 'courses' && courses) {
      const length = courses.length
      return courses.map((course, i) => {
        return (
          <EducationListItem
            key={`${course.id}-${i18n.language}`}
            profileWarningId={`profile-item-courses-${course.id}`}
            editable={editable}
            title={title}
            course={course}
            showDivider={i++ < length - 1}
            onOpen={() => openItem(course)}
            header="course"
            anonymous={anonymous}
          />
        )
      })
    } else return []
  }, [anonymous, courses, editable, openItem, title, i18n.language])

  const certificatesMap = useMemo(() => {
    if (title === 'certificates' && certificates) {
      const length = certificates.length
      return certificates.map((certificate, i) => {
        return (
          <EducationListItem
            key={`${certificate.id}-${i18n.language}`}
            profileWarningId={`profile-item-certificates-${certificate.id}`}
            editable={editable}
            title={title}
            certificate={certificate}
            showDivider={i++ < length - 1}
            onOpen={() => openItem(certificate)}
            header="certificate"
            anonymous={anonymous}
          />
        )
      })
    } else return []
  }, [anonymous, certificates, editable, openItem, title, i18n.language])

  const competencesMap = useMemo(() => {
    if (title === 'competences' && competences && features.includes('competences')) {
      const length = competences.length
      return competences.map((competence, i) => {
        return (
          <EducationListItem
            key={`${competence.id}-${i18n.language}`}
            profileWarningId={`profile-item-competences-${competence.id}`}
            editable={editable}
            title={title}
            competence={competence}
            showDivider={i++ < length - 1}
            onOpen={() => openItem(competence)}
            header="competence"
            anonymous={anonymous}
          />
        )
      })
    } else return []
  }, [anonymous, competences, editable, features, openItem, title, i18n.language])

  const getMargin = (title: string) => {
    if (title === 'competences') {
      return 'none'
    } else {
      return '18px'
    }
  }

  return (
    <>
      <Box
        sx={{
          font: 'Raleway',
          fontWeight: 'bold',
          letterSpacing: '0.9px',
          color: theme => theme.palette.primary.main,
          marginLeft: '12px',
          alignSelf: 'start',
        }}
      >
        {t(`${title}.title`)}
      </Box>
      <Grid
        sx={{
          width: '100%',
          background: theme => theme.palette.background.default,
          borderRadius: '5px',
          padding: '0px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          marginTop: '9px',
          marginBottom: getMargin(title),
        }}
      >
        <Grid
          sx={{
            width: '100%',
            height: '5px',
            background: theme =>
              color === 'info'
                ? theme.palette.info.main
                : color === 'success'
                  ? theme.palette.success.main
                  : theme.palette.warning.main,
          }}
        />
        {educationsMap && educationsMap.length > 0 ? educationsMap : null}
        {coursesMap && coursesMap.length > 0 ? coursesMap : null}
        {certificatesMap && certificatesMap.length > 0 ? certificatesMap : null}
        {features.includes('competences') && competencesMap && competencesMap.length > 0 ? competencesMap : null}
      </Grid>
    </>
  )
}

export default EducationItemCard
